// CourseSidebar — left-side navigation modeled on v1 sidebar.
//
// Layout:
//   Back-to-home link
//   Course title + countdown
//   Nav list (filtered by enabledComponents)
//   Footer: guide icon + language toggle + health indicator + brand

import { type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  BookOpenCheck,
  Bookmark,
  Clock,
  Compass,
  Layers,
  Sparkles,
  AlertTriangle,
  Settings,
  RefreshCcw,
  ClipboardPaste,
  HelpCircle,
  Map as MapIcon,
} from 'lucide-react';
import type { ComponentId } from '@examforge/shared/schemas';
import type { DictionaryKey } from '@examforge/shared/i18n';
import { useT, LanguageToggle } from '../i18n/index.js';
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { daysUntil } from '../lib/days-until.js';
import { NotificationCenter } from '../components/notification-center.js';
import { SyncStatusChip } from '../components/sync-status-chip.js';
import { ThemeToggle } from '../components/theme-toggle.js';

interface NavSpec {
  path: string;
  componentId: ComponentId | 'always' | { anyOf: ComponentId[] };
  labelKey: DictionaryKey;
  icon: ReactNode;
  groupBefore?: DictionaryKey;
  emphasis?: 'exam';
}

const DRILL_COMPONENTS: ComponentId[] = [
  'quiz_mcq', 'quiz_tf', 'proof_drill', 'free_response', 'reading_passage', 'essay',
];

const NAV: readonly NavSpec[] = [
  { path: '',                componentId: 'always',                       labelKey: 'nav.dashboard',     icon: <BookOpen size={15} /> },
  { path: 'drill',           componentId: { anyOf: DRILL_COMPONENTS },    labelKey: 'nav.drill',         icon: <Sparkles size={15} /> },
  { path: 'flashcards',      componentId: 'flashcards',                   labelKey: 'nav.flashcards',    icon: <Layers size={15} /> },
  { path: 'mindmap',         componentId: 'mindmap',                      labelKey: 'nav.mindmap',       icon: <Compass size={15} /> },
  { path: 'glossary',        componentId: 'glossary',                     labelKey: 'nav.glossary',      icon: <BookOpenCheck size={15} /> },
  { path: 'lecture-notes',   componentId: 'lecture_notes',                labelKey: 'nav.lectureNotes',  icon: <Layers size={15} /> },
  { path: 'bookmarks',       componentId: 'always',                       labelKey: 'nav.bookmarks',     icon: <Bookmark size={15} /> },
  { path: 'wrong-answers',   componentId: 'wrong_answers',                labelKey: 'nav.wrongAnswers',  icon: <AlertTriangle size={15} /> },
  // Mock exam — own group.
  { path: 'mock',            componentId: 'mock_exam',                    labelKey: 'nav.mockExam',      icon: <Clock size={15} />,
    groupBefore: 'nav.examModeGroup', emphasis: 'exam' },

  // Management group: outline → regen → settings
  { path: 'outline',         componentId: 'always',                       labelKey: 'nav.outline',       icon: <MapIcon size={15} />,
    groupBefore: 'nav.manageGroup' },
  { path: 'regen',           componentId: 'always',                       labelKey: 'nav.regen',         icon: <RefreshCcw size={15} /> },
  { path: 'settings',        componentId: 'always',                       labelKey: 'nav.settings',      icon: <Settings size={15} /> },
];

export function CourseSidebar() {
  const t = useT();
  const { draft, pkg, switchToPaste } = useStoreCourseData();

  const enabled = new Set<ComponentId>(draft.enabledComponents);
  const items = NAV.filter((n) => {
    if (n.componentId === 'always') return true;
    if (typeof n.componentId === 'string') return enabled.has(n.componentId);
    return n.componentId.anyOf.some((c) => enabled.has(c));
  });
  // E-2 (19BM): daysUntil util handles null + NaN internally — invalid examDate
  // no longer leaks NaN into the countdown UI (was the spec §3.5 bug).
  const days = daysUntil(draft.examDate);

  return (
    <aside className="course-sidebar">
      <div className="course-sidebar-top">
        <Link to="/" className="course-sidebar-back" aria-label={t('course.backHome')}>
          <span aria-hidden="true">←</span>
          <span>{t('course.backHome')}</span>
        </Link>
        <h1 className="course-sidebar-title" title={draft.title}>{draft.title}</h1>
        {/* Session 3 (19BM-E): course code area — clears DEBT-SIDEBAR-COURSE-CODE.
         *  Optional field; show muted prompt when unset (non-blocking). */}
        {draft.code ? (
          <span className="course-sidebar-code">{draft.code}</span>
        ) : (
          <span className="course-sidebar-code is-unset">{t('sidebar.courseCodeUnset')}</span>
        )}
        {days !== null ? (
          <span className={`course-sidebar-countdown${days <= 7 && days >= 0 ? ' is-urgent' : ''}${days < 0 ? ' is-past' : ''}`}>
            {days > 0
              ? (<>
                  {days <= 7 && <span className="course-sidebar-soon">{t('dashboard.examComingSoon')}</span>}
                  {days} {t('dashboard.daysUntilExam')}
                </>)
              : days === 0
                ? t('dashboard.examToday')
                : t('dashboard.examPassed')}
          </span>
        ) : (
          <span className="course-sidebar-countdown is-muted">
            {t('form.step1.examDateUnknown')}
          </span>
        )}
        <p className="course-sidebar-meta">
          {pkg.cases.length} cases · {pkg.flashcards.length} cards · {pkg.mcqs.length} MCQs
        </p>
      </div>

      <ul className="course-sidebar-nav" role="list">
        {items.map((n) => (
          <li key={n.path}>
            {n.groupBefore && (
              <div className="course-sidebar-group" aria-hidden="true">
                <span className="course-sidebar-group-label">{t(n.groupBefore)}</span>
              </div>
            )}
            <NavLink
              to={n.path}
              end={n.path === ''}
              className={({ isActive }) =>
                `course-sidebar-link${isActive ? ' is-active' : ''}${n.emphasis ? ' is-emphasis-' + n.emphasis : ''}`
              }
            >
              <span className="course-sidebar-icon" aria-hidden="true">{n.icon}</span>
              <span className="course-sidebar-label">{t(n.labelKey)}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="course-sidebar-footer">
        <div className="course-sidebar-footer-row">
          <LanguageToggle />
          <ThemeToggle />
          <Link to="/guide" className="course-sidebar-footer-icon" title={t('guide.linkText')} aria-label={t('guide.linkText')}>
            <HelpCircle size={14} />
          </Link>
          <NotificationCenter variant="sidebar" />
          <SyncStatusChip variant="sidebar" />
          <span className="course-sidebar-health" title={t('health.checking')}>
            <Activity size={14} />
          </span>
        </div>
        <button
          type="button"
          className="course-sidebar-paste-btn"
          onClick={switchToPaste}
          title={t('course.pasteJsonAlt')}
        >
          <ClipboardPaste size={13} />
          <span>{t('course.pasteJsonAlt')}</span>
        </button>
        <Link to="/" className="course-sidebar-brand">
          <span className="course-sidebar-brand-zh">{t('app.name')}</span>
          <span className="course-sidebar-brand-en">{t('app.nameEn')}</span>
        </Link>
      </div>
    </aside>
  );
}
